/** One sibling option in a multi-option estimate (rows sharing a MutiEstiIdentifier). */
export interface ThreadEstimateOptionDto {
  estimateKey: string;
  /** The vendor's title for the option, or "Option N" when it has none. */
  optionLabel: string;
  total: number;
  /** Raw VendorEstimate.Status: 1 approved, 3 closed / declined, anything else still open. */
  status?: number;
  statusLabel?: string;
  /** MCEstimate == 1: the group's anchor row, NOT a vendor recommendation (not shown). */
  isVendorPick?: boolean;
  isSelected?: boolean;
}

/** Response from ProjectRCS GET /JobOpsChat/GetThreadEstimate */
export interface ThreadEstimateDto {
  ok: boolean;
  error?: string;
  message?: string;
  hasEstimate?: boolean;
  estimateKey?: string;
  jobKey?: string;
  vendorKey?: string;
  status?: number;
  statusLabel?: string;
  onsiteApproval?: boolean;
  html?: string;
  originalHtml?: string;
  hasComparison?: boolean;
  allowedActions?: string[];
  canAct?: boolean;
  approveDisabled?: boolean;
  approveDisabledReason?: string;
  /** Minimum-markup DNE rule: set whenever approving is blocked (the exact alert text). */
  approveDneMessage?: string;
  approveHardBlocked?: boolean;
  /** Optional extras, null/absent unless blocked: X = vendor amount + minimum profit. */
  approveDneRequiredTotal?: number | null;
  /** Y = the customer's (revised) DNE. */
  approveDneCustomerDne?: number | null;
  /** Z = the most the vendor could ask and still stay under the DNE. */
  approveDneMaxVendorAmount?: number | null;
  /** The minimum profit the customer's minimum markup asks for (X - vendor amount). */
  approveDneMinimumProfit?: number | null;
  /** The minimum markup % behind it. */
  approveDneMarkupPercent?: number | null;
  estimateOptionCount?: number;
  /** Populated only when there's more than one sibling estimate on this job/vendor. */
  options?: ThreadEstimateOptionDto[];
  reviewingPosted?: boolean;
  isCancelled?: boolean;
  needsReview?: boolean;
  vendorResubmitted?: boolean;
  createdInvoiceForIncurredAfterDeclined?: boolean;
  hasUnbilledIncurredLines?: boolean;
  invoiceTotal?: number;
  oldTotal?: number | null;
  totalsChanged?: boolean;
  editedByVendor?: boolean;
  isEdited?: boolean;
  isNew?: boolean;
  hasArchive?: boolean;
  archiveVersion?: number | null;
  vendorClearedReject?: boolean;
  previousEstimateLabel?: string;
  /** Relative path — MgtVendorInvoice/UpdateVendorEstimate (full V1 vendor estimate page). */
  manageUrl?: string;
  /** Relative path — MgtVendorInvoice/EIndex for all estimates on the job. */
  billsUrl?: string;
  /** Relative path to the vendor→customer estimate split-screen builder (Get Customer Approval flow). */
  splitScreenUrl?: string;
  /**
   * Where the customer side stands. 'customer_changes_requested': the customer rejected the customer estimate
   * for resubmission, so it is to be edited and re-sent (a change order) rather than created again.
   * Other values are passed through untouched; absent for an ordinary estimate.
   */
  customerApprovalState?: string;
  /** The EXISTING customer estimate key (set with 'customer_changes_requested'). */
  customerEstimateKey?: string;
}

/** The thread estimate's customerApprovalState once the customer sent the estimate back for changes. */
export const CUSTOMER_CHANGES_REQUESTED = 'customer_changes_requested';

export interface OnsiteEstimateActionRequest {
  estimateKey: string;
  action: 'approve' | 'reject' | 'decline';
  adminRemark?: string;
  forceApprove?: boolean;
  /** Customer-DNE check: true = "Keep all vendors" (every vendor's estimate on the job counts). */
  keepAllVendors?: boolean;
  /**
   * "Override and approve vendor anyway with reduced profit" (Lance, 3 Nov): approve past the customer-DNE block. Job Ops
   * accepts it only when the vendor is under the DNE and the profit still meets the customer's minimum margin.
   */
  dneOverrideRemark?: string;
  /** Why it may be approved although the vendor is at or above the DNE. */
  dneReason?: string;
}

/** Shared request shape for the two customer-approval actions that only need an estimateKey. */
export interface CustomerApprovalActionRequest {
  estimateKey: string;
}

export interface GetCustomerApprovalResult {
  ok: boolean;
  error?: string;
  message?: string;
  /** Vendor→customer estimate split-screen builder URL to open in a new tab. */
  splitScreenUrl?: string;
  /** 'customer_changes_requested' when the customer sent the estimate back: nothing is created, edit the existing one. */
  customerApprovalState?: string;
  /** The existing customer estimate to edit and re-send (with 'customer_changes_requested'). */
  customerEstimateKey?: string;
  /** True for that change-order case. */
  changeOrder?: boolean;
}

export interface ApproveOnBehalfAndVendorRequest {
  estimateKey: string;
  /** Required — how the customer approved (contact name, verbal approval, or reference to uploaded proof). */
  customerConversation: string;
}

export interface OnsiteEstimateActionResult {
  ok: boolean;
  error?: string;
  message?: string;
  messageKey?: string;
}

export interface GoHomeRequest {
  estimateKey: string;
  adminRemark?: string;
}

/** Line from GET /JobOpsChat/GetOnsiteNegotiateLines */
export interface OnsiteNegotiateLine {
  detailKey: string;
  labor: 'material' | 'labor';
  itemName?: string;
  chargeTypeKey?: string;
  description?: string;
  rate?: number;
  qty?: number;
  hour?: number;
  tech?: number;
  costIncurred?: number;
  display?: number;
  /** False for labor and trip charge — admin may only change qty/hours/tech. */
  rateEditable?: boolean;
}

export interface OnsiteNegotiateLinesResult {
  ok: boolean;
  error?: string;
  message?: string;
  estimateKey?: string;
  jobKey?: string;
  vendorKey?: string;
  lines?: OnsiteNegotiateLine[];
}

export interface OnsiteNegotiateRequest {
  estimateKey: string;
  adminRemark?: string | null;
  lines: OnsiteNegotiateLine[];
  keepAllVendors?: boolean;
}

export interface OnsiteNegotiateResult {
  ok: boolean;
  error?: string;
  message?: string;
}

/** Line from GET /JobOpsChat/GetOnsiteDeclineIncurredLines */
export interface OnsiteDeclineIncurredLine {
  detailKey: string;
  whichTable: number;
  itemName?: string;
  description?: string;
  amount?: number;
  qty?: number;
  rowTotal?: number;
}

export interface OnsiteDeclineIncurredLinesResult {
  ok: boolean;
  error?: string;
  message?: string;
  estimateKey?: string;
  jobKey?: string;
  vendorKey?: string;
  hasIncurredLines?: boolean;
  alreadyInvoiced?: boolean;
  lines?: OnsiteDeclineIncurredLine[];
}

export interface OnsiteDeclineIncurredSelection {
  detailKey: string;
  whichTable: number;
}

export interface OnsiteDeclineIncurredInvoiceRequest {
  estimateKey: string;
  jobKey: string;
  vendorKey: string;
  detailList: OnsiteDeclineIncurredSelection[];
}

export interface OnsiteDeclineIncurredInvoiceResult {
  ok: boolean;
  error?: string;
  message?: string;
  vendorInvoiceKey?: string;
  invoiceUrl?: string;
}
